// StyleSheet for the shared MaxesView + EditMaxSheet. Lifted verbatim from
// apps/mobile's maxesStyles; takes a UiTheme so it renders identically on
// native and the web-rnw console.
import { StyleSheet } from "react-native";
import { webTapFloor, type UiTheme } from "../theme";

export function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    safe: { flex: 1 },
    flex: { flex: 1 },
    header: {
      flexDirection: "row",
      alignItems: "center",
      paddingHorizontal: s.s5,
      paddingVertical: s.s4,
      gap: s.s4,
    },
    backHit: { minHeight: 44, justifyContent: "center" },
    back: { color: p.ink2, fontSize: t.sizes.base },
    headerSpacer: { width: 56 },
    title: {
      flex: 1,
      color: p.ink,
      fontSize: t.sizes.xl,
      fontWeight: "600",
      letterSpacing: -0.4,
    },
    searchWrap: {
      paddingHorizontal: s.s5,
      paddingBottom: s.s3,
    },
    search: {
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      color: p.ink,
      fontSize: t.sizes.base,
      paddingHorizontal: s.s4,
      paddingVertical: s.s3,
    },
    row: {
      flexDirection: "row",
      alignItems: "center",
      paddingHorizontal: s.s5,
      paddingVertical: s.s4,
      gap: s.s4,
    },
    rowPressed: {
      backgroundColor: p.surface2,
    },
    // Persistent highlight for the row whose edit form fills the wide
    // detail pane (a tap flash isn't enough — the selection outlives it).
    // brand100 tinted fill, paired with accessibilityState selected so the
    // cue is never colour-alone.
    rowSelected: {
      backgroundColor: p.brand100,
    },
    rowName: {
      flex: 1,
      color: p.ink,
      fontSize: t.sizes.base,
      fontWeight: "500",
    },
    // Colour budget: the MAX value is a static hero number, so emphasis comes
    // from mono weight + size, not the brand orange (reserved for the one
    // primary action + the live/now state).
    rowValue: {
      color: p.ink,
      fontSize: t.sizes.base,
      fontWeight: "700",
      fontVariant: ["tabular-nums"],
    },
    rowValueEmpty: {
      // ink2: it is a word now («Not set»), not a dash, and it has to be read.
      color: p.ink2,
      fontWeight: "400",
    },
    separator: {
      height: StyleSheet.hairlineWidth,
      marginLeft: s.s5,
    },
    center: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      padding: s.s5,
    },
    errorText: {
      color: p.danger,
      fontSize: t.sizes.sm,
    },
    emptyText: {
      color: p.ink3,
      fontSize: t.sizes.base,
      textAlign: "center",
    },
    editBody: {
      paddingHorizontal: s.s5,
      paddingVertical: s.s4,
      gap: s.s4,
    },
    inputRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s3,
    },
    input: {
      flex: 1,
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      color: p.ink,
      fontSize: t.sizes.lg,
      fontVariant: ["tabular-nums"],
      paddingHorizontal: s.s4,
      paddingVertical: s.s3,
    },
    clearAction: {
      borderColor: p.danger,
      borderWidth: 1,
      borderRadius: r.r3,
      paddingVertical: s.s4,
      minHeight: webTapFloor ?? 40,
      alignItems: "center",
      justifyContent: "center",
      marginTop: s.s4,
    },
    clearActionPressed: {
      backgroundColor: p.dangerFill,
    },
    clearActionDisabled: {
      opacity: 0.4,
    },
    clearActionText: {
      color: p.danger,
      fontSize: t.sizes.base,
      fontWeight: "600",
    },
    // --- Wide master-detail (web-rnw console; mobile never hits these) ---
    masterRow: { flex: 1, flexDirection: "row" },
    listPane: {
      width: 380,
      flexShrink: 0,
      borderRightWidth: 1,
      borderRightColor: p.border,
    },
    detailPane: { flex: 1, minWidth: 0 },
    paneHeading: {
      color: p.ink,
      fontSize: t.sizes.xl,
      fontWeight: "700",
      letterSpacing: -0.4,
      paddingHorizontal: s.s5,
      paddingTop: s.s4,
      paddingBottom: s.s3,
    },
    // A real placeholder (directional cue + readable instruction at body
    // contrast), not a blank pane — mirrors the Catalog detail empty state.
    empty: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      padding: s.s5,
      gap: s.s3,
    },
    emptyGlyph: {
      color: p.ink3,
      fontSize: 44,
      lineHeight: 44,
      fontWeight: "300",
    },
    emptyTitle: {
      color: p.ink2,
      fontSize: t.sizes.lg,
      fontWeight: "600",
      textAlign: "center",
      maxWidth: 280,
    },
    // Primary Save button — only rendered inline in the wide detail pane
    // (narrow keeps Save in the modal header).
    saveBtn: {
      backgroundColor: p.brand500,
      borderRadius: r.r3,
      paddingVertical: s.s4,
      minHeight: webTapFloor ?? 40,
      alignItems: "center",
      justifyContent: "center",
      marginTop: s.s2,
    },
    saveBtnPressed: { backgroundColor: p.brand600 },
    saveBtnDisabled: { opacity: 0.4 },
    saveBtnText: {
      color: p.inkOnBrand,
      fontSize: t.sizes.base,
      fontWeight: "700",
    },
  });
}
